import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { MyTaskEntry } from '../../../data/client';
import { formatDate } from '../../../fields/format';
import {
  cx,
  ExpandableText,
  ExpandableTextToggle,
  Icon,
  STATUS_INK,
  StatusSymbol,
  useExpandableText,
  useIsDesktop,
  WAITING_ON_CLIENT,
} from '../../../ui';
import { formatMoney } from '../clients/clientModel';
import { INTAKES_PATH, type IntakesToReviewCount } from '../intake/useIntakesToReview';
import { clientHref, LoadErrorLine, stepActorOf } from '../shared';
import { Commitments } from './Commitments';
import { DashboardColumn } from './DashboardColumn';
import {
  daysBetween,
  dayWords,
  dueSoonWords,
  intakesWords,
  type MoneyBuckets,
  type OnboardingRow,
  otherTasksWords,
  type PaymentFacts,
  pendingWords,
  type TaskLists,
} from './model';

/**
 * TODAY, THE RIGHT COLUMN (register P1, L4, H2; brief section 6; mockup `:237-274`; components
 * page section 3, "Decided: no boxes"): headings above, spacing between, no group boxes.
 *
 * Top to bottom: the two task lists, Money overdue with the Promised group and the 7-day line,
 * Onboarding with the intakes line and the three most late board clients, and the quiet lines
 * taken over from Home, each only when its count is above zero (R4).
 */
export const ROWS_PER_BLOCK = 3;

export interface TodayMoney {
  readonly buckets: MoneyBuckets;
  /** `/objects/<payment object id>`: "See all <N>" and the tile (MoneyPanel's link). */
  readonly listHref: string;
  readonly packageOf: (agencyId: string | null) => string | null;
}

export interface TodayOnboarding {
  readonly rows: readonly OnboardingRow[];
  /** The count and whether it could be read (H7: a failed count is no zero). */
  readonly intakesToReview: IntakesToReviewCount;
  /** "Intakes checked <relative>", the intake data age (D9). */
  readonly age?: ReactNode;
}

export interface TodayColumnProps {
  readonly today: string;
  readonly tasks: TaskLists<MyTaskEntry>;
  /** Absent for a person who may not read payments: the block is not drawn. */
  readonly money?: TodayMoney | undefined;
  readonly onboarding?: TodayOnboarding | undefined;
  /** "<N> changes waiting for your approval": the count, only for a person holding PENDING_DECIDE. */
  readonly pending?: number | undefined;
}

export function TodayColumn({ today, tasks, money, onboarding, pending }: TodayColumnProps) {
  const isDesktop = useIsDesktop();
  const quiet = [
    pending !== undefined && pending > 0 ? (
      <QuietLink key="pending" to="/pending">
        {pendingWords(pending)}
      </QuietLink>
    ) : null,
    tasks.otherDue > 0 ? (
      <QuietLink key="tasks" to="/tasks">
        {otherTasksWords(tasks.otherDue)}
      </QuietLink>
    ) : null,
  ].filter((line) => line !== null);

  const body = (
    <div className="pb-2" data-today-body="">
      <Commitments lists={tasks} today={today} />
      {money !== undefined && <MoneyBlock money={money} today={today} />}
      {onboarding !== undefined && <OnboardingBlock onboarding={onboarding} />}
      {quiet.length > 0 && (
        <div
          className="mt-8 divide-y divide-line-subtle border-y border-line-subtle"
          data-quiet-lines=""
        >
          {quiet}
        </div>
      )}
    </div>
  );

  if (isDesktop) {
    return (
      <DashboardColumn name="today" title="Today">
        {body}
      </DashboardColumn>
    );
  }
  return (
    <section aria-label="Today" data-dashboard-section="today">
      <h2 className="pb-2 text-sm font-semibold text-ink">Today</h2>
      {body}
    </section>
  );
}

function BlockHead({ title, right }: { readonly title: string; readonly right?: ReactNode }) {
  return (
    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
      {title}
      {right !== undefined && <span className="ms-auto text-2xs font-normal">{right}</span>}
    </h3>
  );
}

function QuietLink({ to, children }: { readonly to: string; readonly children: ReactNode }) {
  return (
    <Link
      to={to}
      className="flex min-h-(--touch-target-min) items-center py-2.5 text-sm text-accent"
    >
      {children}
    </Link>
  );
}

/**
 * MONEY OVERDUE (brief section 6; A12 variant B): the overdue payments nobody has a promise
 * for, oldest first, three rows; then "Promised · <N>" with the promise date instead of the
 * days and the note as a quote; then the money due in the next 7 days. Not filtered by owner
 * (Q-DB-8). Drawn only when one of the three has something.
 */
function MoneyBlock({ money, today }: { readonly money: TodayMoney; readonly today: string }) {
  const { buckets } = money;
  const dueSoon = buckets.dueSoonTotal;
  if (buckets.overdueCount === 0 && dueSoon === null) return null;
  return (
    <section aria-label="Money overdue" data-today-block="money" className="mt-8">
      <BlockHead
        title="Money overdue"
        right={
          buckets.overdueCount > 0 ? (
            <Link to={money.listHref} className="text-accent">
              See all {String(buckets.overdueCount)}
            </Link>
          ) : undefined
        }
      />
      {buckets.overdue.length > 0 && (
        <ul className="divide-y divide-line-subtle border-t border-line-subtle">
          {buckets.overdue.slice(0, ROWS_PER_BLOCK).map((payment) => (
            <PaymentRow
              key={payment.id}
              payment={payment}
              packageLabel={money.packageOf(payment.agencyId)}
              right={
                payment.dueDate === null ? null : (
                  <span className={cx('font-semibold', STATUS_INK['act-now'])}>
                    {dayWords(daysBetween(payment.dueDate, today))}
                  </span>
                )
              }
            />
          ))}
        </ul>
      )}
      {buckets.promised.length > 0 && (
        <>
          <h4 className="flex items-center gap-2 pt-4 pb-1.5 text-2xs font-semibold text-ink-muted">
            Promised
            <span className="font-normal text-ink-subtle">{`· ${String(buckets.promised.length)}`}</span>
          </h4>
          <ul className="divide-y divide-line-subtle border-t border-line-subtle">
            {buckets.promised.map((payment) => (
              <PaymentRow
                key={payment.id}
                payment={payment}
                packageLabel={money.packageOf(payment.agencyId)}
                right={
                  payment.promisedAt === null ? null : (
                    <span className="text-ink">{formatDate(payment.promisedAt.slice(0, 10))}</span>
                  )
                }
                note={payment.promiseNote}
              />
            ))}
          </ul>
        </>
      )}
      {dueSoon !== null && (
        <p className="pt-2.5 text-sm text-ink-muted" data-money-due-soon="">
          {dueSoonWords(dueSoon)}
        </p>
      )}
    </section>
  );
}

function PaymentRow({
  payment,
  packageLabel,
  right,
  note,
}: {
  readonly payment: PaymentFacts;
  readonly packageLabel: string | null;
  readonly right: ReactNode;
  readonly note?: string | null | undefined;
}) {
  const expandable = useExpandableText();
  const body = (
    <>
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
        <span className="font-semibold text-ink">{payment.clientName ?? 'Untitled'}</span>
        {packageLabel !== null && <span className="text-2xs text-ink-muted">{packageLabel}</span>}
      </span>
      <span className="text-right text-sm text-ink tabular-nums">
        {payment.amount === null ? '' : formatMoney(payment.amount)}
      </span>
      <span className="text-right text-sm tabular-nums">{right}</span>
      <Icon name="chevron-right" className="self-center text-ink-subtle" />
      {note !== null && note !== undefined && (
        <span className="col-span-3 text-2xs text-ink-muted italic">
          <ExpandableText lines={2} state={expandable}>
            {`“${note}”`}
          </ExpandableText>
        </span>
      )}
    </>
  );
  const grid =
    'grid min-h-(--touch-target-min) min-w-0 grid-cols-[minmax(0,1fr)_auto_auto_auto] items-baseline gap-x-3 gap-y-0.5 py-2.5';
  return (
    <li>
      {payment.agencyId === null ? (
        <div className={grid}>{body}</div>
      ) : (
        <Link
          to={clientHref(payment.agencyId)}
          className={cx('pressable -mx-2 cursor-default px-2 hover:bg-surface-hover', grid)}
        >
          {body}
        </Link>
      )}
      {/* "More" for a long promise note stands under the link, never inside it, and names the
          row's client so the Dashboard's several "More" buttons differ (BR-PARTS-18). */}
      <ExpandableTextToggle
        state={expandable}
        subject={payment.clientName ?? 'Untitled'}
        className="-mt-1.5 mb-1.5"
      />
    </li>
  );
}

/**
 * ONBOARDING (brief section 6): "Open" to the board, the count of the intakes nobody has reviewed
 * linking to their list, the one the Onboarding page's "Review intakes" opens (register A6-12,
 * "never a settings page"; BR-INTAKE-1), with
 * the intake data age, and the three most late board clients with the late step, its days and
 * "Waiting on client" when the step is the client's (D4, A9).
 */
function OnboardingBlock({ onboarding }: { readonly onboarding: TodayOnboarding }) {
  return (
    <section aria-label="Onboarding" data-today-block="onboarding" className="mt-8">
      <BlockHead
        title="Onboarding"
        right={
          <Link to="/fulfillment/onboarding" className="text-accent">
            Open
          </Link>
        }
      />
      {onboarding.intakesToReview.isError && (
        /* A failed count is no zero (H7, BR-FIX item 3): the link stays, without a number,
           under the line that says so. */
        <div className="mb-2 space-y-1">
          <LoadErrorLine what="The intakes" onRetry={onboarding.intakesToReview.retry} />
          <p className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-sm">
            <Link to={INTAKES_PATH} className="text-accent">
              Review intakes
            </Link>
            {onboarding.age}
          </p>
        </div>
      )}
      {!onboarding.intakesToReview.isError &&
        (onboarding.intakesToReview.count > 0 || onboarding.age !== undefined) && (
          <p className="mb-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-sm">
            {onboarding.intakesToReview.count > 0 && (
              <Link to={INTAKES_PATH} className="text-accent">
                {intakesWords(onboarding.intakesToReview.count)}
              </Link>
            )}
            {onboarding.age}
          </p>
        )}
      {onboarding.rows.length > 0 && (
        <ul className="divide-y divide-line-subtle border-t border-line-subtle">
          {onboarding.rows.map((row) => (
            <OnboardingRowView key={row.enrollment.id} row={row} />
          ))}
        </ul>
      )}
    </section>
  );
}

function OnboardingRowView({ row }: { readonly row: OnboardingRow }) {
  const name = row.agency?.name ?? row.enrollment.agencyLabel ?? 'Untitled';
  const waitingOnClient = stepActorOf(row.step.name) === 'client';
  const body = (
    <>
      <span className="min-w-0">
        <span className="block font-semibold text-ink">{name}</span>
        <span className="block text-2xs text-ink-muted">{row.step.label}</span>
        {waitingOnClient && (
          <span className="block text-2xs text-ink-muted">{WAITING_ON_CLIENT}</span>
        )}
      </span>
      {/* A step the client owes draws the waiting mark in its tone, ours the late clock
          (decision page item 5.1; the Checklist's cell, BR-NIGHT-b). */}
      <span
        className={cx(
          'inline-flex items-center gap-1 text-sm font-semibold tabular-nums',
          waitingOnClient ? STATUS_INK.waiting : STATUS_INK.late,
        )}
      >
        <StatusSymbol status={waitingOnClient ? 'waiting' : 'late'} />
        {`${dayWords(row.step.daysLate ?? 0)} late`}
      </span>
      <Icon name="chevron-right" className="self-center text-ink-subtle" />
    </>
  );
  const grid =
    'grid min-h-(--touch-target-min) min-w-0 grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 py-2.5';
  return (
    <li>
      {row.agency === undefined ? (
        <div className={grid}>{body}</div>
      ) : (
        <Link
          to={clientHref(row.agency.id)}
          className={cx('pressable -mx-2 cursor-default px-2 hover:bg-surface-hover', grid)}
        >
          {body}
        </Link>
      )}
    </li>
  );
}
